<script>
  // # # # # # # # # # # # # #
  //
  //  Feed module
  //
  // # # # # # # # # # # # # #

  // *** IMPORT
  import { onMount } from "svelte";
  import { Router, links } from "svelte-routing";
  import { fly, fade } from "svelte/transition";
  import { quadOut } from "svelte/easing";
  import imagesLoaded from "imagesloaded";
  import has from "lodash/has";

  // *** COMPONENTS
  import Taglist from "./Taglist.svelte";
  import PopUp from "./PopUp.svelte";

  // *** PROPS
  export let item;

  // *** DOM REFERENCES
  let feedItemEl = {};
  let videoEl = {};

  // *** VARIABLES
  let loaded = false;
  let active = false;
  let revealCaption = false;
  let paused = false;
  let sizes = 0;
  let fullScreen = false;
  let url = "";
  let videoUrl = "";

  let src = "";
  let videoSrc = "";
  let posterImageSrc = "";
  let srcset = "";
  let ROOT = "https://res.cloudinary.com/pwr/image/upload/";
  let VIDEO_ROOT = "https://res.cloudinary.com/pwr/video/upload/";
  let REMOTE_FOLDER = "completeworks";

  // *** CONSTANTS
  const fullWidthParams = ",ar_1.7,c_fill,g_auto,f_auto,q_90/";
  const imgParams = ",c_scale,f_auto,q_auto/";

  // *** FUNCTIONS
  const toggleVideo = () => {
    if (videoEl && has(videoEl, "play") && has(videoEl, "pause")) {
      paused ? videoEl.play() : videoEl.pause();
    }
  };

  if (item.header.video) {
    videoUrl = item.header.video.url.replace("https://completeworks.ch", "");
    videoSrc = VIDEO_ROOT + REMOTE_FOLDER + encodeURI(videoUrl);
    posterImageSrc = videoSrc.substring(0, videoSrc.length - 4) + ".jpg";
  } else {
    // Set base image sizestr
    if (item.header.featured_image) {
      url = item.header.featured_image.sizes.original.replace(
        "https://completeworks.ch",
        ""
      );
    } else {
      url = "";
    }

    src =
      ROOT + "w_" + 1800 + item.header.layout == "full"
        ? fullWidthParams
        : imgParams + REMOTE_FOLDER + encodeURI(url);

    // Generate srcset
    srcset = ["", 600, 800, 1000, 1200, 1400, 1600, 2000].reduce(
      (result, size) => {
        return (
          result +
          ROOT +
          "w_" +
          size +
          (item.header.layout == "full" ? fullWidthParams : imgParams) +
          REMOTE_FOLDER +
          encodeURI(url) +
          " " +
          size +
          "w, "
        );
      }
    );
  }

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.intersectionRatio > 0.2) {
          active = true;
        }
        if (entry.intersectionRatio > 0.1) {
          if (videoEl && videoEl.play) {
            let promise = videoEl.play();
            if (promise !== undefined) {
              promise
                .then(_ => {
                  console.log("video started");
                })
                .catch(error => {
                  console.log(error);
                });
            }
          }
        }
        if (entry.intersectionRatio < 0.1) {
          if (videoEl && videoEl.pause) {
            console.log("video paused");
            videoEl.pause();
          }
        }
      });
    },
    { threshold: [0, 0.2, 0.3, 1] }
  );

  // *** ON MOUNT
  onMount(async () => {
    observer.observe(feedItemEl);
    imagesLoaded(feedItemEl, instance => {
      loaded = true;
    });
  });
</script>

<style lang="scss">
  @import "../_variables.scss";

  .feed-item {
    $block: &;

    display: flex;
    height: 600px;
    opacity: 0;
    position: relative;
    transition: opacity 0.7s ease-out, transform 0.7s ease-out;
    width: 100%;

    @include screen-size("small") {
      display: inline-block;
    }

    &.active {
      opacity: 1;
    }

    &:first-child {
      transform: scale(0.99);
      transition: opacity 1s ease-out, transform 1s ease-out;
      &.active {
        opacity: 1;
        transform: scale(1);
      }
    }

    &__image {
      height: 100%;
      opacity: 1;
      overflow: hidden;
      position: relative;
      width: 100%;
      z-index: 10;
      cursor: pointer;

      img {
        display: block;
        height: 100%;
        object-fit: cover;
        pointer-events: none;
        width: 100%;

        @include screen-size("small") {
          height: unset;
        }
      }

      @include media-hover-state;

      @include screen-size("small") {
        height: unset;
      }
    }

    &__video {
      cursor: pointer;
      height: 100%;
      opacity: 1;
      overflow: hidden;
      position: relative;
      transition: opacity $fast-easing;
      width: 100%;
      z-index: 10;

      &:hover {
        .video-play {
          opacity: 0.9;
          transition: opacity 0.3s ease-out;
        }
      }

      img,
      video {
        display: block;
        height: 100%;
        object-fit: cover;
        pointer-events: none;
        width: 100%;
      }

      @include screen-size("small") {
        height: unset;
      }
    }

    &__text {
      height: 100%;
      font-size: $font-size-large;
      opacity: 1;
      width: 50%;
      z-index: 1;

      @include screen-size("small") {
        font-size: $mobile-font-size-medium;

        height: auto;
        width: 100%;
      }
    }

    &__caption {
      max-width: 90%;
      font-family: $serif-stack;
      max-height: 95%;
      overflow: scroll;
      padding: $small-margin;
      padding-bottom: 80px;
      // padding-right: 10px;
      width: 35ch;
      z-index: 9;

      @include hide-scroll;

      p {
        margin-top: 0;
        margin-bottom: $small-margin;
        margin-bottom: $main-margin;
      }

      @include screen-size("small") {
        width: 45ch;
        padding-bottom: 20px;
      }
    }

    &__header {
      font-weight: 500;
    }

    &__blur {
      position: absolute;
      width: 100%;
      height: 60px;
      background: $white;
      box-shadow: -7px -30px 40px $white;
      bottom: 0;
      left: 0;
      @include screen-size("small") {
        display: none;
      }
    }

    &.right {
      #{ $block }__image,
      #{ $block }__video {
        order: 2;
        // clip-path: inset(0 0% 0 0%);
        // transition: clip-path $fast-easing;

        @include screen-size("small") {
          order: 1;
        }

        -webkit-clip-path: inset(0 0% 0 0%);
        clip-path: inset(0 0% 0 0%);
        transition: clip-path $fast-easing;
        // opacity: 0.6;

        &.hidden {
          // opacity: 0.2;
          -webkit-clip-path: inset(0 50% 0 0%);

          clip-path: inset(0 50% 0 0%);
          @include screen-size("small") {
            -webkit-clip-path: inset(0 0% 0 0%);
            clip-path: inset(0 0% 0 0%);
          }
        }
      }
      #{ $block }__text {
        order: 1;
        text-align: right;

        @include screen-size("small") {
          order: 2;
        }
      }

      #{ $block }__caption {
        float: right;
      }
    }

    &.left {
      #{ $block }__image,
      #{ $block }__video {
        order: 1;
        // clip-path: inset(0 0% 0 0%);
        // transition: clip-path $fast-easing;

        -webkit-clip-path: inset(0 0% 0 0%);
        clip-path: inset(0 0% 0 0%);
        transition: clip-path $fast-easing;
        // opacity: 0.6;

        &.hidden {
          // opacity: 0.2;
          -webkit-clip-path: inset(0 50% 0 0%);
          clip-path: inset(0 50% 0 0%);
        }
      }

      #{ $block }__text {
        order: 2;
        text-align: left;

        @include screen-size("small") {
          left: 20%;
        }
      }

      #{ $block }__caption {
        float: left;
      }
    }

    &--full {
      height: 100vh;
      cursor: pointer;

      @include screen-size("small") {
        height: unset;
        // max-height: 80vh;
      }

      #{ $block }__text {
        position: absolute;
        top: 0;
        left: 50%;
        // text-align: left !important;

        @include screen-size("small") {
          left: unset;
          position: static;
        }
      }

      #{ $block }__video {
        @include screen-size("small") {
          pointer-events: all;
          width: 100vw;
          height: 100vw;
          // height: unset;
        }
      }
    }

    &--half {
      height: 100vh;

      @include screen-size("small") {
        width: 100vw;
        height: unset;
        // max-height: 80vh;
      }

      #{ $block }__image {
        width: 50%;
        height: 100%;

        @include screen-size("small") {
          pointer-events: all;
          width: 100%;
          height: unset;
        }
      }

      #{ $block }__video {
        width: 50%;
        height: 100%;

        @include screen-size("small") {
          pointer-events: all;
          width: 100vw;
          height: 100vw;
        }
      }
    }
  }

  .video-play {
    position: absolute;
    top: 50%;
    z-index: 1000;
    height: 200px;
    width: 200px;
    left: 50%;
    margin-left: -80px;
    margin-top: -100px;
    color: white;
    opacity: 0.6;
    pointer-events: none;
    transition: opacity 0.3s ease-out;
  }
</style>

<Router>

  <div
    class="feed-item feed-item--{item.header.layout}"
    class:active={active && loaded}
    class:fullwidth={item.header.layout == 'full'}
    class:left={item.header.alignment == 'left'}
    class:right={item.header.alignment == 'right'}
    on:click={e => {
      if (item.header.layout == 'full' && item.content.length > 0) {
        revealCaption = !revealCaption;
      } else if (!item.header.link) {
        fullScreen = true;
      }
    }}
    bind:this={feedItemEl}
    use:links>
    <!-- {# VIDEO #} -->
    {#if item.header.video}
      <div
        class="feed-item__video"
        on:click={() => {
          fullScreen = true;
        }}>
        <video
          muted
          loop
          src={videoSrc}
          bind:this={videoEl}
          poster={posterImageSrc}
          bind:paused />
        {#if paused}
          <svg
            class="video-play"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            stroke-width="0.2">
            <circle cx="12" cy="12" r="10" />
            <polygon points="10 8 16 12 10 16 10 8" />
          </svg>
        {/if}
      </div>
      <!-- {# IMAGE #} -->
    {:else if item.header.featured_image}
      {#if item.header.link}
        <a href={item.header.link} class="feed-item__image">
          <img
            {srcset}
            {src}
            sizes="(max-width: 700px) 100vw, 50vw"
            alt={item.header.title} />
        </a>
      {:else}
        <div class="feed-item__image" class:hidden={revealCaption}>
          {#if item.header.layout == 'full'}
            <img {srcset} {src} sizes="100vw" alt={item.header.title} />
          {:else}
            <img
              {srcset}
              {src}
              sizes="(max-width: 700px) 100vw, 50vw"
              alt={item.header.title} />
          {/if}
        </div>
      {/if}
    {/if}

    <!-- {# TEXT #} -->
    <div class="feed-item__text feed-item__text--shown">

      {#if active && loaded}
        <!-- {# CAPTION #} -->
        <div
          class="feed-item__caption"
          in:fade={{ duration: 300, delay: 200, easing: quadOut }}>
          {@html item.content}
          <Taglist {item} />
        </div>
      {/if}

      <div class="feed-item__blur" />

    </div>

  </div>

</Router>

{#if fullScreen}
  <PopUp
    {item}
    on:close={() => {
      fullScreen = false;
    }} />
{/if}
